import clsx from 'clsx';
import moment from 'moment';
import { hasMicrophone } from 'detectrtc';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useTimer from '../../../hooks/useTimer';

import StdOverlay from '../../atoms/StdOverlay';
import StdIcon from '../../atoms/StdIcon';

import './camera.scss';

export function displayTimer(secondsCounter: number) {
    const seconds = secondsCounter % 60;

    const minutesCounter = Math.floor(secondsCounter / 60);
    const minutes = minutesCounter % 60;

    const hours = Math.floor(minutesCounter / 60);

    const hourString = hours.toString().padStart(2, '0');
    const minuteString = minutes.toString().padStart(2, '0');
    const secondsString = seconds.toString().padStart(2, '0');

    return `${hourString}:${minuteString}:${secondsString}`;
}

const hasVideoCaputre = Boolean(window.MediaRecorder);
const hasImageCapture = Boolean((window as any).ImageCapture);

// Safari's MediaRecorder does not support video/webm (VP8/VP9) — only
// video/mp4 (H.264), and only since Safari 14.1. Chrome/Firefox support webm
// but not mp4 recording. Ask the browser what it can actually produce instead
// of assuming webm everywhere (ASW-1485 — a hardcoded webm Blob on Safari
// produced an undecodable file with no error, silently breaking preview and
// upload).
const VIDEO_MIME_CANDIDATES = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4'
];

export function getSupportedVideoMimeType(): string {
    if (!window.MediaRecorder?.isTypeSupported) return '';
    return VIDEO_MIME_CANDIDATES.find((type) =>
        MediaRecorder.isTypeSupported(type)
    ) ?? '';
}

export function videoFileExtension(mimeType: string): string {
    return mimeType.includes('mp4') ? 'mp4' : 'webm';
}

// Need to wait until detectRTC library has loaded its checks
// The assumption here is that Camera component will only be rendered if the libary is loaded
// When a deviceId is given we request that exact camera — facingMode switching
// is unreliable on desktop, cycling deviceIds via enumerateDevices works (ASW-1483).
const getCameraParams = (deviceId?: string): MediaStreamConstraints => {
    return {
        audio: hasMicrophone, // If microphone is avialable request audio as well
        video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            ...(deviceId
                ? { deviceId: { exact: deviceId } }
                : { advanced: [{ facingMode: 'environment' }] })
        }
    };
};

interface IProps {
    open?: boolean;
    onClose?: ICallback;
    onFileCreated?: (file: File) => void;
}

const Camera: React.FC<IProps> = ({ open, onClose, onFileCreated }) => {
    const { t } = useTranslation();

    const { counter, startTimer, stopTimer, resetTimer } = useTimer();

    const hasRequestedRef = useRef(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const recordedBlobRef = useRef<Blob | null>(null);
    // The mimeType actually used for the current recording (ASW-1485) — set
    // when recording starts, read back when the file is built so the Blob/File
    // type always matches the real encoded bytes instead of an assumption.
    const recordedMimeTypeRef = useRef<string>('video/webm');

    const pictureCanvasRef = useRef<HTMLCanvasElement>(null);
    const videoPreviewRef = useRef<HTMLVideoElement>(null);

    const [hasTakenPicture, setHasTakenPicture] = useState(false);

    const [isRecordingVideo, setIsRecordingVideo] = useState(false);
    const [hasRecoredVideo, setHasRecordedVideo] = useState(false);

    // All available camera deviceIds — used by the switch-camera button (ASW-1483).
    const [videoDeviceIds, setVideoDeviceIds] = useState<string[]>([]);
    const currentDeviceIdRef = useRef<string | null>(null);

    // Wire a stream up to the preview video element and canvas.
    const attachStream = useCallback((stream: MediaStream) => {
        const videoEl = videoRef.current;
        const pictureCanvas = pictureCanvasRef.current;
        if (!videoEl || !pictureCanvas) return;

        const settings = stream.getVideoTracks()[0].getSettings();
        const { width = 680, height = 480 } = settings;
        currentDeviceIdRef.current = settings.deviceId ?? null;

        videoEl.width = width;
        videoEl.height = height;

        // set canvas dimentions
        pictureCanvas.width = width;
        pictureCanvas.height = height;

        videoEl.setAttribute('autoplay', '');
        videoEl.setAttribute('muted', '');
        videoEl.setAttribute('playsinline', '');

        videoEl.muted = true;
        videoEl.srcObject = stream;
        videoEl.play();

        streamRef.current = stream;
    }, []);

    const getUserMedia = useCallback(() => {
        if (!videoRef.current) return;
        if (!pictureCanvasRef.current) return;

        // if the cleanup hasn't run, the function is called
        // due to dependecies, ignore it
        if (hasRequestedRef.current) return;
        hasRequestedRef.current = true;

        navigator.mediaDevices
            .getUserMedia(getCameraParams())
            .then((stream) => {
                attachStream(stream);

                // Device labels/ids are only fully available after permission
                // was granted. If enumeration fails the switch button simply
                // stays hidden — not a reason to close the camera.
                navigator.mediaDevices
                    .enumerateDevices()
                    .then((devices) => {
                        setVideoDeviceIds(
                            devices
                                .filter(
                                    (device) =>
                                        device.kind === 'videoinput' &&
                                        device.deviceId
                                )
                                .map((device) => device.deviceId)
                        );
                    })
                    .catch(() => setVideoDeviceIds([]));
            })
            .catch((_) => {
                alert(t('camera_access_fail_message'));
                onClose?.();
            });
    }, [t, onClose, attachStream]);

    // Cycle through the available cameras by deviceId (ASW-1483).
    const switchCamera = useCallback(() => {
        if (videoDeviceIds.length < 2) return;

        const stream = streamRef.current;
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }

        const currentIndex = videoDeviceIds.indexOf(
            currentDeviceIdRef.current ?? ''
        );
        const nextDeviceId =
            videoDeviceIds[(currentIndex + 1) % videoDeviceIds.length];

        navigator.mediaDevices
            .getUserMedia(getCameraParams(nextDeviceId))
            .then(attachStream)
            .catch((_) => {
                alert(t('camera_access_fail_message'));
                onClose?.();
            });
    }, [videoDeviceIds, attachStream, t, onClose]);

    const takePicture = useCallback(() => {
        const stream = streamRef.current;
        const videoEl = videoRef.current;

        const pictureCanvas = pictureCanvasRef.current;
        if (!pictureCanvas) return;

        const ctx = pictureCanvas.getContext('2d');
        if (!ctx) return;

        if (stream && hasImageCapture) {
            // The new API
            const capture = new ImageCapture(stream.getVideoTracks()[0]);
            capture.grabFrame().then((bitmap) => {
                ctx.clearRect(0, 0, pictureCanvas.width, pictureCanvas.height);
                ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height);

                setHasTakenPicture(true);
            });
        } else if (videoEl) {
            // The old API
            ctx.clearRect(0, 0, pictureCanvas.width, pictureCanvas.height);
            ctx.drawImage(videoEl, 0, 0, videoEl.width, videoEl.height);

            setHasTakenPicture(true);
        }
    }, []);

    const onPictureSave = useCallback(() => {
        const pictureCanvas = pictureCanvasRef.current;
        if (!pictureCanvas) return;

        pictureCanvas.toBlob((blob) => {
            if (!blob) return;

            const timeStamp = moment().unix();
            const file = new File([blob], `camera_picture_${timeStamp}.png`, {
                type: 'image/png',
                lastModified: timeStamp
            });

            onFileCreated?.(file);
        });
        onClose?.();
    }, [onClose, onFileCreated]);

    const recordVideo = useCallback(() => {
        const recordedBlobs: Blob[] = [];

        const stream = streamRef.current;
        const videoPreview = videoPreviewRef.current;
        if (!stream || !videoPreview) return;

        const mimeType = getSupportedVideoMimeType();
        const recorder = new MediaRecorder(
            stream,
            mimeType ? { mimeType } : undefined
        );
        recorderRef.current = recorder;
        // recorder.mimeType reflects what the browser actually picked, which
        // may differ from our preferred candidate — trust it over our guess.
        recordedMimeTypeRef.current = recorder.mimeType || mimeType || 'video/webm';

        recorder.ondataavailable = (event) => {
            recordedBlobs.push(event.data);
        };

        recorder.onstop = () => {
            const superBuffer = new Blob(recordedBlobs, {
                type: recordedMimeTypeRef.current
            });

            videoPreview.src = '';
            videoPreview.srcObject = null;
            videoPreview.src = window.URL.createObjectURL(superBuffer);
            videoPreview.controls = true;

            recordedBlobRef.current = superBuffer;

            setHasRecordedVideo(true);
            setIsRecordingVideo(false);
        };

        recorder.start();
        startTimer();
        setIsRecordingVideo(true);
    }, [startTimer]);

    const stopRecordingVideo = useCallback(() => {
        stopTimer();

        const recorder = recorderRef.current;
        if (!recorder) return;

        recorder.stop();
    }, [stopTimer]);

    const cancelRecordedVideo = useCallback(() => {
        recordedBlobRef.current = null;
        setHasRecordedVideo(false);
        resetTimer();
    }, [resetTimer]);

    const saveRecordedVideo = useCallback(() => {
        const recordedBlob = recordedBlobRef.current;
        if (!recordedBlob) return;

        const timeStamp = moment().unix();
        const mimeType = recordedMimeTypeRef.current;
        const extension = videoFileExtension(mimeType);
        const file = new File(
            [recordedBlob],
            `camera_video_${timeStamp}.${extension}`,
            {
                type: mimeType,
                lastModified: timeStamp
            }
        );

        onFileCreated?.(file);
        onClose?.();
    }, [onClose, onFileCreated]);

    const freeUserMedia = useCallback(() => {
        const videoEl = videoRef.current;
        const stream = streamRef.current;
        const recorder = recorderRef.current;

        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }

        if (recorder) {
            recorder.ondataavailable = null;
            recorder.onstop = null;
            recorderRef.current = null;
        }

        if (videoEl) {
            videoEl.pause();

            videoEl.srcObject = null;
            videoEl.load();
        }

        setHasTakenPicture(false);
        cancelRecordedVideo();
        stopRecordingVideo();
        setIsRecordingVideo(false);

        hasRequestedRef.current = false;
    }, [stopRecordingVideo, cancelRecordedVideo]);

    useEffect(() => {
        if (open) getUserMedia();
    }, [open, getUserMedia, freeUserMedia]);

    // Cleanup on unmount
    useEffect(() => {
        return () => freeUserMedia();
    }, [freeUserMedia]);

    const hideInitActions =
        hasTakenPicture || hasRecoredVideo || isRecordingVideo;
    return (
        <StdOverlay open={Boolean(open)} onExited={freeUserMedia}>
            <div className="camera__container">
                <video ref={videoRef} className="camera__output" />
                <canvas
                    ref={pictureCanvasRef}
                    className={clsx(
                        'camera__picture-preview',
                        !hasTakenPicture && 'camera__picture-preview--hidden'
                    )}
                />
                <video
                    ref={videoPreviewRef}
                    className={clsx(
                        'camera__picture-preview',
                        !hasRecoredVideo && 'camera__picture-preview--hidden'
                    )}
                />
                <div className="camera__controls">
                    <StdIcon
                        className="inline-m"
                        name="arrow-left"
                        onClick={onClose}
                        clickable
                    />
                    {hasTakenPicture && (
                        <>
                            <StdIcon
                                className="inline-m"
                                name="check-circle"
                                onClick={onPictureSave}
                                clickable
                            />
                            <StdIcon
                                className="inline-m"
                                name="times-circle"
                                onClick={() => setHasTakenPicture(false)}
                                clickable
                            />
                        </>
                    )}
                    {hasRecoredVideo && (
                        <>
                            <StdIcon
                                className="inline-m"
                                name="check-circle"
                                onClick={saveRecordedVideo}
                                clickable
                            />
                            <StdIcon
                                className="inline-m"
                                name="times-circle"
                                onClick={cancelRecordedVideo}
                                clickable
                            />
                        </>
                    )}
                    {isRecordingVideo && (
                        <>
                            <StdIcon
                                className="inline-m"
                                name="stop-circle"
                                onClick={stopRecordingVideo}
                                clickable
                            />
                            <span className="inline-m">
                                {displayTimer(counter)}
                            </span>
                        </>
                    )}
                    {!hideInitActions && (
                        <>
                            <StdIcon
                                className="inline-m"
                                name="camera"
                                onClick={takePicture}
                                clickable
                            />
                            {hasVideoCaputre && (
                                <StdIcon
                                    className="inline-m"
                                    name="video"
                                    onClick={recordVideo}
                                    clickable
                                />
                            )}
                            {videoDeviceIds.length > 1 && (
                                <StdIcon
                                    className="inline-m"
                                    name="sync-alt"
                                    onClick={switchCamera}
                                    clickable
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </StdOverlay>
    );
};

export default Camera;
